import React, { FC } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Loading from '../system/Loading'
import ErrorComponent from '../system/Error'
import { listPublicVideos, PublicVideoSummary } from '../../system/apitools'
import useRouter from '../../hooks/useRouter'

export const publicThumbnailUrl = (video: PublicVideoSummary): string | undefined =>
  video.has_thumbnail ? `/api/v1/public/videos/${video.id}/thumbnail/image` : undefined

const VideoCard: FC<{ video: PublicVideoSummary }> = ({ video }) => {
  const { navigate } = useRouter()
  const [thumbFailed, setThumbFailed] = React.useState(false)
  const thumbUrl = publicThumbnailUrl(video)
  // A video id is still a valid container key — ResolveContainer's fourth
  // step — so the public page resolves it and redirects to the canonical
  // slug. The listing has no slug of its own to send.
  const watch = () => navigate('video', { key: video.id })

  return (
    <Box
      onClick={watch}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: 1,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      {/* Square media box — screenshots/thumbnails are square (quadrant grid) */}
      <Box sx={{ position: 'relative', width: '100%', pt: '100%', backgroundColor: '#222' }}>
        {thumbUrl && !thumbFailed ? (
          <Box
            component="img"
            src={thumbUrl}
            alt={video.name || 'Video'}
            onError={() => setThumbFailed(true)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              No preview
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {video.name || 'Untitled'}
        </Typography>
        {video.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {video.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {new Date(video.created_at).toLocaleDateString()}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={(e) => { e.stopPropagation(); watch() }}
          sx={{ mt: 1, alignSelf: 'flex-start' }}
        >
          Watch
        </Button>
      </Box>
    </Box>
  )
}

const VideoGallery: FC = () => {
  const { isPending, error, data: videos } = useQuery({
    queryKey: ['public-videos'],
    queryFn: listPublicVideos,
  })

  if (isPending) return <Loading />
  if (error) return <ErrorComponent message={(error as Error).message} />

  if (!videos || videos.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
        No videos yet.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 3 }}>
      {videos.map((video) => (
        <VideoCard key={video.id} video={video} />
      ))}
    </Box>
  )
}

export default VideoGallery
